import * as Sentry from "@sentry/nuxt";
import { useAuthStore } from "~/store/auth/auth.store";
import {
  isAuthTelemetryEventSuppressed,
  isAuthTelemetrySuppressed,
  sanitizeAuthTelemetry,
} from "~/utils/helpers/auth/privacy";

export default defineNuxtPlugin((nuxtApp) => {
  const authStore = useAuthStore();
  const environment = nuxtApp.$config.public.ENV;
  const appVersion = nuxtApp.$config.public.APP_VERSION;

  if (!["DEV", "PROD"].includes(environment)) return;
  let initialized = false;
  function initialize() {
    if (initialized || isAuthTelemetrySuppressed()) return;
    initialized = true;
    Sentry.setUser({
      id: authStore.user?.userId ?? "-",
    });
    Sentry.init({
      // debug: true,
      dsn: nuxtApp.$config.public.SENTRY_DSN,
      environment,
      ...(appVersion && { release: appVersion }),
      integrations: [
        // Sentry v9 moved these Vue options out of Sentry.init() into vueIntegration();
        // trackComponents/timeout now live under tracingOptions, attachProps stays top-level.
        Sentry.vueIntegration({
          attachProps: false,
          tracingOptions: {
            trackComponents: true,
            timeout: 2000,
          },
        }),
        Sentry.browserTracingIntegration(),
        Sentry.browserProfilingIntegration(),
        Sentry.piniaIntegration(usePinia(), {
          attachPiniaState: false, // Disable to prevent attachment quota consumption
          addBreadcrumbs: false,
        }),
        Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
          networkCaptureBodies: false,
          networkDetailDenyUrls: [/\/(?:auth|oauth|oidc|sso)(?:\/|\?|$)/i],
          networkRequestHeaders: [],
          networkResponseHeaders: [],
        }),
      ],
      sendDefaultPii: false,
      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: environment === "PROD" ? 0.5 : 0,
      replaysSessionSampleRate: environment === "PROD" ? 1 : 0,
      replaysOnErrorSampleRate: environment === "PROD" ? 1 : 0,
      profilesSampleRate: 0.5,

      beforeBreadcrumb(breadcrumb) {
        if (isAuthTelemetrySuppressed() || isAuthTelemetryEventSuppressed({ request: { url: breadcrumb.data?.url } })) {
          return null;
        }
        return sanitizeAuthTelemetry(breadcrumb);
      },
      beforeSend(event) {
        if (isAuthTelemetrySuppressed() || isAuthTelemetryEventSuppressed(event)) return null;
        Sentry.setUser({
          id: authStore.user?.userId ?? "-",
        });
        return sanitizeAuthTelemetry(event);
      },
      beforeSendTransaction(event) {
        if (isAuthTelemetrySuppressed() || isAuthTelemetryEventSuppressed(event)) return null;
        return sanitizeAuthTelemetry(event);
      },
    });
  }
  initialize();
  nuxtApp.$router.beforeEach(async (to) => {
    if (isAuthTelemetrySuppressed(to.fullPath)) await Sentry.getReplay()?.stop();
  });
  nuxtApp.$router.afterEach((to) => {
    if (isAuthTelemetrySuppressed(to.fullPath)) return;
    initialize();
    if (environment === "PROD") Sentry.getReplay()?.start();
  });
});
